/**
 * Centralized App Configuration
 * 
 * This file contains global app flags and configuration settings.
 * The PRODUCTION_MODE flag controls logging behavior across the entire app.
 */

import Constants from 'expo-constants';

/**
 * Feature Flag Names
 * Store all feature flag names in this enum for consistency
 */
export enum FeatureFlags {
  PRODUCTION_MODE = 'PRODUCTION_MODE',
  ENABLE_CANCEL_DELIVERY = 'ENABLE_CANCEL_DELIVERY',
}

/**
 * App Configuration
 * 
 * PRODUCTION_MODE: When true, ALL logging is disabled including:
 * - console.log, console.warn, console.error, console.debug, console.info
 * - logger.* calls
 * - Any other logging utilities
 * 
 * To enable production mode:
 * 1. Set EXPO_PUBLIC_PRODUCTION_MODE=true in your .env file
 * 2. Or set it directly in eas.json for builds
 * 
 * Note: Errors are still captured by Sentry even when logs are disabled.
 */
export const APP_CONFIG = {
  /**
   * Production Mode Flag
   * 
   * When true: All console/logging output is suppressed
   * When false: Normal logging behavior based on __DEV__
   * 
   * Priority: Environment variable > Default (false)
   */
  PRODUCTION_MODE: (() => {
    // Check environment variable first
    const envValue = Constants.expoConfig?.extra?.EXPO_PUBLIC_PRODUCTION_MODE 
      ?? process.env.EXPO_PUBLIC_PRODUCTION_MODE;
    
    // Parse boolean from string if needed
    if (typeof envValue === 'string') {
      return envValue.toLowerCase() === 'true';
    }
    if (typeof envValue === 'boolean') {
      return envValue;
    }
    
    // Default to false (logging enabled)
    return false;
  })(),

  /**
   * Development Mode Flag (read-only, from React Native)
   * This is automatically set by the build system
   */
  get IS_DEV(): boolean {
    return __DEV__;
  },

  /**
   * Enable Cancel Delivery Feature
   */
  ENABLE_CANCEL_DELIVERY: (() => {
    const envValue = Constants.expoConfig?.extra?.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY 
      ?? process.env.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY;
    
    if (typeof envValue === 'string') {
      return envValue.toLowerCase() === 'true';
    }
    if (typeof envValue === 'boolean') {
      return envValue;
    }
    return false;
  })(),
} as const;

/**
 * Helper to check if logging should be enabled
 * Returns true only if:
 * - PRODUCTION_MODE is false
 * - AND we're in development mode (__DEV__ is true)
 */
export const isLoggingEnabled = (): boolean => {
  return !APP_CONFIG.PRODUCTION_MODE && APP_CONFIG.IS_DEV;
};

/**
 * Helper to check if we're in strict production mode
 * (no logging at all, even for errors)
 */
export const isStrictProductionMode = (): boolean => {
  return APP_CONFIG.PRODUCTION_MODE;
};

/**
 * Validate that feature flag values are expected
 * Call this at app startup for safety
 */
export const validateFeatureFlags = (): void => {
  const productionMode = APP_CONFIG.PRODUCTION_MODE;
  
  if (typeof productionMode !== 'boolean') {
    console.error(
      `[AppConfig] Invalid PRODUCTION_MODE value: ${productionMode}. Expected boolean.`
    );
  }
};

export default APP_CONFIG;

